// Translating a `patients_with_ehrid` (roster) row into the `notes-c` (visit)
// field names the schedule forms are keyed on.
//
// The two tables spell the same three facts differently, which is why quick-add
// auto-fill used to leave boxes blank whenever the row it picked came from the
// roster rather than a visit:
//
//   form field   notes-c     patients_with_ehrid
//   ──────────   ─────────   ─────────────────────────────
//   EHR ID       EHR_ID      ehr_id          (different case)
//   Phone        tel_num     phone_numbers   (jsonb array)
//   Email        email       email           (the one that agreed)
//
// Postgres identifiers are case-sensitive, so `ehr_id` and `EHR_ID` are two
// different columns and each table has only one of them.
//
// A raw roster row spread into a notes-c insert therefore does more than
// under-fill the form. Verified against the live schema, six roster columns do
// not exist on notes-c at all and the insert is rejected outright:
//
//   ehr_id  meds  phone_numbers  source_record_ids  updated_at  visit_dates
//
// Two more DO exist on notes-c and are still dropped, for reasons of their own:
//
//   vita_id  — notes-c has it (nullable, non-unique, assigned by the
//              BEFORE INSERT trigger trg_notes-c_vita_id). Let the trigger
//              resolve it from the fields actually submitted. Copying the
//              roster's value would win over the trigger — it returns early
//              when vita_id is already set — so editing the name after picking
//              a suggestion would file the new visit under the wrong patient.
//   user_id — means different things in the two tables: the PATIENT's portal
//              account on the roster, the row's owner on notes-c.
//
// rosterSuggestionFields() is an allowlist rather than a list of those eight,
// so a roster column added later cannot leak into a visit insert either.

// Roster rows hold phones as a jsonb array of either bare strings or
// `{ number }` objects; notes-c holds a single `tel_num` string.
function firstPhoneNumber(phoneNumbers) {
  const phones = Array.isArray(phoneNumbers) ? phoneNumbers : [];
  const first = phones[0];
  if (typeof first === 'string') return first;
  return first?.number || '';
}

/**
 * The roster→visit field translation, in ONE place. Both the patient-level
 * chart (mapRosterRowToPatient) and the quick-add suggestion list read it
 * through here so the two can no longer drift apart.
 *
 * Returns ONLY the translated fields — callers that also want the row's raw
 * roster columns (source_record_ids, meds, vita_id, …) spread the row
 * themselves.
 */
export function rosterChartFields(row) {
  return {
    id: row.id,
    patient_name: row.patient_name || '',
    // Kept verbatim, including a `VITA-…` placeholder: until staff enter a real
    // chart number that placeholder IS this patient's identifier, and blanking
    // it would just make someone invent one.
    EHR_ID: row.ehr_id || null,
    email: row.email || '',
    tel_num: firstPhoneNumber(row.phone_numbers),
    // A roster row is patient-level — it has no appointment of its own.
    date_: null,
    time_: null,
    dob: row.dob ?? null,
    type: row.type || null,
    more_audios: Array.isArray(row.more_audios) ? row.more_audios : [],
    transcripts: Array.isArray(row.transcripts) ? row.transcripts : [],
    reports: row.reports || null,
  };
}

// What a roster row is allowed to contribute to a schedule form. An allowlist,
// not a blocklist: every one of these is a real notes-c column (verified
// against the live schema), so whatever columns the roster grows later can
// never reach a visit insert through here.
//
// `type` is deliberately absent even though rosterChartFields() supplies it —
// the roster has no `type` column, so it would always arrive as null and
// silently clear the role's default Type on the form.
const SUGGESTION_KEYS = [
  'id',
  'patient_name',
  'EHR_ID',
  'email',
  'tel_num',
  'dob',
  'date_',
  'time_',
];

/** A roster row reduced to the notes-c-shaped fields a schedule form can use. */
export function rosterSuggestionFields(row) {
  const translated = rosterChartFields(row || {});
  return Object.fromEntries(SUGGESTION_KEYS.map((k) => [k, translated[k]]));
}

/**
 * Normalize one suggestion row for the quick-add dropdown.
 *
 * Visit rows pass through untouched — they are already notes-c rows, so they
 * are correct by construction and carry fields the form wants (type,
 * physician_name, template) that the roster has no equivalent for.
 */
export function normalizeSuggestionRow(row) {
  if (!row || row._source !== 'roster') return row;
  return {
    ...rosterSuggestionFields(row),
    _source: 'roster',
    ...(row._schema ? { _schema: row._schema } : {}),
  };
}

// The fields quick-add is expected to fill in. Contact details are entered
// inconsistently across a patient's rows — staff type a phone number onto one
// visit and an email onto the next, and a portal signup lands on the roster row
// with neither — so picking a single representative row loses whichever
// details happen to live elsewhere.
export const SUGGESTION_CONTACT_FIELDS = ['email', 'tel_num', 'EHR_ID', 'dob'];

function isBlank(value) {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

/**
 * Fill the representative row's blank contact fields from the patient's other
 * rows.
 *
 * The representative always wins where it has a value — it is the most recent
 * visit, i.e. the last thing staff actually typed. `rows` supplies the
 * fallbacks and is expected in preference order (visits newest-first, then the
 * roster row), so the roster only ever backfills what no visit recorded.
 */
export function coalesceSuggestionContact(rep, rows = []) {
  const merged = { ...rep };
  for (const field of SUGGESTION_CONTACT_FIELDS) {
    if (!isBlank(merged[field])) continue;
    const donor = rows.find((r) => !isBlank(r?.[field]));
    if (donor) merged[field] = donor[field];
  }
  return merged;
}
